import React from "react";

import MenuIcon from "@uui/DotsHorizontal";
import SettingsIcon from "@uui/Settings04";

import Divider from "@mui/material/Divider";

import { FloatingNavIconButton } from "@app/analytics/components/reports/FloatingNav";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import type {
  MeetingReportDialog,
  RecapMainSection,
  RecapSection,
} from "@app/analytics/MeetingReport/types";
import { EditIcon, FilterIcon } from "@app/common/assets/icons";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";
import { titleize } from "@app/utils/utils";

import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Column } from "@rui/Stack";

import CopyNotes from "./CopyNotes";
import { RestoreMenuItems } from "./RestoreMenu";
import SearchNavAction from "./SearchNavAction";

const MobileSubNavMenu = ({
  onEnterEditMode,
  notesCopyContent,
  selectedSection,
  showRestore,
  showSettings,
  showTranscriptFilters,
}: {
  onEnterEditMode?: (section: RecapMainSection) => void;
  notesCopyContent?: string;
  selectedSection: RecapSection;
  showRestore: boolean;
  showSettings: boolean;
  showTranscriptFilters: boolean;
}) => {
  const { open, onClick, onClose, anchorEl } = useAnchorElement();
  const setDialog = useMeetingReportUiStore((state) => state.setDialog);

  const editAccessLevel = useEditStore((state) => state.editAccessLevel);

  if (selectedSection === "highlights" || selectedSection === "chapters") {
    return null;
  }

  const openDialog = (dialog: MeetingReportDialog) => () => {
    setDialog(dialog);
    onClose();
  };

  const canEditTranscript =
    selectedSection === "transcript" && editAccessLevel === "full";
  const canEditNotes =
    selectedSection === "notes" && editAccessLevel !== "none";

  const showEditMenuItem =
    onEnterEditMode != null && (canEditNotes || canEditTranscript);

  return (
    <>
      <FloatingNavIconButton Icon={MenuIcon} onClick={onClick} />
      <Menu open={open} onClose={onClose} anchorEl={anchorEl} padding="8px">
        <Column onClick={onClose} minWidth="200px">
          <SearchNavAction variant="menu" />
          {showSettings && (
            <MenuItem
              icon={<SettingsIcon />}
              onClick={openDialog("recap_settings")}
            >
              Settings
            </MenuItem>
          )}
          {notesCopyContent && (
            <CopyNotes content={notesCopyContent} variant="menu" />
          )}
          {showTranscriptFilters && (
            <MenuItem
              icon={<FilterIcon />}
              onClick={openDialog("transcript_filters")}
            >
              Filter transcript
            </MenuItem>
          )}
          {(showEditMenuItem || showRestore) && (
            <Column display="contents">
              <Divider sx={{ my: 1 }} />
              {showEditMenuItem && (
                <MenuItem
                  icon={<EditIcon />}
                  onClick={() => onEnterEditMode(selectedSection)}
                >
                  Edit {titleize(selectedSection)}
                </MenuItem>
              )}
              {showRestore && (
                <RestoreMenuItems onClick={onClose} section={selectedSection} />
              )}
            </Column>
          )}
        </Column>
      </Menu>
    </>
  );
};

export default MobileSubNavMenu;
